import React, { useState, useEffect } from 'react';
import logo from '../../assets/Logo.png'
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { FaLinkedin, FaEnvelope, FaArrowUp } from 'react-icons/fa';
import header from '../../assets/header.png'

const PrivacyPolicy = () => {
    const [showScrollButton, setShowScrollButton] = useState(false);
  
    useEffect(() => {
      const handleScroll = () => {
        if (window.pageYOffset > 300) {
          setShowScrollButton(true);
        } else {
          setShowScrollButton(false);
        }
      };
  
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);
  
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
       {/* Scroll to top button */}
      {showScrollButton && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          aria-label="Scroll to top"
        >
          <FaArrowUp className="text-white text-xl" />
        </motion.button>
      )}
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="w-auto flex-shrink-0">
            <Link to="/">
              <img
                src={header}
                alt="Vorstel logo"
                width="140"
                height="35"
                className="w-[140px] h-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              />
            </Link>
          </div>
          <div className={`flex items-center space-x-4`}>
            <a href="mailto:sales@vorstel.com" className="p-1" aria-label="Email Vorstel sales team">
              <FaEnvelope className="text-lg sm:text-xl text-gray-700 hover:text-blue-500" aria-hidden="true" />
            </a>
            <a href="https://www.linkedin.com/company/vorstel-gmbh/about/" target="_blank" rel="noopener noreferrer" className="p-1" aria-label="Vorstel on LinkedIn (opens in a new tab)">
              <FaLinkedin className="text-lg sm:text-xl text-gray-700 hover:text-blue-500" aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Main content card */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Hero section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-12 px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-3">Privacy Policy</h1>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Effective: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Content with sticky sidebar */}
          <div className="flex flex-col lg:flex-row">
            {/* Sticky sidebar navigation */}
            <div className="lg:w-1/4 p-6 bg-gray-50 border-r border-gray-200 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto">
              <h3 className="font-semibold text-gray-700 mb-4">Quick Navigation</h3>
              <ul className="space-y-3">
                {[
                  'Introduction',
                 'Information We Collect',
                  'How We Use Data',
                  'Data Sharing',
                  'Your Rights',
                  'Data Security',
                  'Policy Changes',
                  'Contact Us'
                ].map((item) => (
                  <li key={item}>
                    <a
                      href={`#${item.toLowerCase().replace(' ', '-')}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Main content */}
            <div className="lg:w-3/4 p-8">
              <div className="prose prose-blue max-w-none">
                {/* Introduction */}
                <section id="introduction" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">1.</span> Introduction
                  </h2>
                  <div className="space-y-4 text-gray-600">
                    <p>
                      Welcome to Vorstel ("Company", "we", "our", "us"). These Terms of Service ("Terms") govern your use of our website and services ("Services").
                    </p>
                    <p>
                      By accessing or using our Services, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the Services.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-500">
                      <p className="font-medium text-blue-800">Please read carefully:</p>
                      <p className="text-blue-700">By using our services, you agree to the collection and use of information in accordance with this policy.</p>
                    </div>
                  </div>
                </section>


                {/* Information We Collect */}
                <section id="information-we-collect" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">2.</span> Information We Collect
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    <p>
                      When you use our Site, we may collect the following types of information:
                    </p>

                    {/* Personal Information */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Personal Information:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Contact details (name, email, phone number) provided via forms</li>
                        <li>Professional information when applying for jobs or partnerships</li>
                      </ul>
                    </div>

                    {/* Technical Data */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Technical Data:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>IP address and general location data</li>
                        <li>Browser type and version</li>
                        <li>Operating system and device information</li>
                        <li>Network connection data</li>
                      </ul>
                    </div>

                    {/* Usage Data */}
                    <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                      <h3 className="text-lg font-semibold text-purple-800 mb-2">Usage Data:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Pages visited and time spent on each</li>
                        <li>Navigation paths through the Site</li>
                        <li>Referral sources and campaign data</li>
                        <li>Search queries on our Site</li>
                        <li>Feature usage and interaction patterns</li>
                      </ul>
                    </div>

                    {/* Cookies/Tracking Data */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Cookies/Tracking Data:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Data from cookies and similar technologies</li>
                        <li>Pixel tag/clear GIF information</li>
                        <li>Analytics data (see our  Cookie Policy)</li>
                        <li>Preferences and settings information</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="font-medium">Collection Methods:</p>
                      <p>Information may be collected directly from you or automatically through your use of the Site, including through cookies and similar tracking technologies.</p>
                    </div>
                  </div>
                </section>


                {/* How We Use Your Information */}
                <section id="how-we-use-data" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">3.</span> How We Use Your Information
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    <p>
                      We use the information we collect for the following purposes:
                    </p>

                    {/* Respond & Provide Services */}
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Respond & Provide Services:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Process and respond to your inquiries and requests</li>
                        <li>Deliver products, services, or information you request</li>
                        <li>Provide customer support and service communications</li>
                        <li>Authenticate user accounts and process transactions</li>
                      </ul>
                    </div>

                    {/* Improve Functionality */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Improve Functionality:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Optimize website performance and user experience</li>
                        <li>Test and improve our Site features and content</li>
                        <li>Personalize your experience based on preferences</li>
                        <li>Develop new products, services, and functionality</li>
                      </ul>
                    </div>

                    {/* Marketing */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Marketing (Opt-in Only):</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Send newsletters, updates, and promotional materials (only with explicit consent)</li>
                        <li>Notify you about special offers and new services</li>
                        <li>Measure effectiveness of our marketing campaigns</li>
                        <li>You may opt-out at any time using unsubscribe links</li>
                      </ul>
                    </div>

                    {/* Analytics */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Analytics:</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Analyze traffic patterns and user behavior</li>
                        <li>Measure Site performance using tools like Google Analytics</li>
                        <li>Understand demographic information about our users</li>
                        <li>Track effectiveness of our content and marketing</li>
                      </ul>
                    </div>

                    {/* Legal Basis */}
                    <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                      <h3 className="text-lg font-semibold text-purple-800 mb-2">Legal Basis for Processing:</h3>
                      <p className="mb-2">We process your information based on:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li><span className="font-medium">Contract performance:</span> When necessary to fulfill our obligations to you</li>
                        <li><span className="font-medium">Legitimate interests:</span> For Site optimization and business improvements</li>
                        <li><span className="font-medium">Your consent:</span> Specifically for marketing communications</li>
                      </ul>
                      <p className="mt-3 text-sm">You may withdraw consent at any time where applicable.</p>
                    </div>
                  </div>
                </section>

                {/* Data Sharing */}
                <section id="data-sharing" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">4.</span> Data Sharing
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <p className="font-medium text-blue-800">
                        We do not sell your personal information. We may share it only in these limited circumstances:
                      </p>
                    </div>

                    {/* Trusted Service Providers */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Trusted Service Providers:</h3>
                      <p className="mb-3">With vendors who perform services for us (e.g., hosting, analytics, email delivery, customer support), strictly under binding contracts that:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Limit use to specified purposes</li>
                        <li>Require data protection equivalent to ours</li>
                        <li>Prohibit retention/sharing beyond service delivery</li>
                      </ul>
                    </div>

                    {/* Legal & Regulatory Obligations */}
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Legal & Regulatory Obligations:</h3>
                      <p className="mb-3">When required by law, including to:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Comply with court orders, subpoenas, or government requests</li>
                        <li>Defend against legal claims</li>
                        <li>Prevent fraud, security breaches, or illegal activity</li>
                        <li>Protect our rights, property, or safety</li>
                      </ul>
                    </div>

                    {/* Corporate Transactions */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Corporate Transactions:</h3>
                      <p>
                        In connection with mergers, acquisitions, or asset sales, where user data may transfer to successors/affiliates under confidentiality agreements.
                      </p>
                    </div>

                    {/* Aggregate/Anonymous Data */}
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Aggregate/Anonymous Data:</h3>
                      <p>
                        We may share non-identifiable, aggregated data (e.g., usage trends) publicly or with partners for analysis.
                      </p>
                    </div>

                    {/* International Transfers */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">International Transfers:</h3>
                      <p>
                        Where applicable, international data transfers comply with data protection laws via appropriate safeguards like EU Standard Contractual Clauses or other approved mechanisms.
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="font-medium">Data Minimization:</p>
                      <p>We only share the minimum necessary personal information required for each specific purpose.</p>
                    </div>
                  </div>
                </section>

                {/* Your Rights */}
                <section id="your-rights" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">5.</span> Your Rights
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    <p>
                      You have the following rights regarding your personal information:
                    </p>

                    {/* Rights Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Access */}
                      <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                        <h3 className="text-lg font-semibold text-blue-800 mb-2">Access</h3>
                        <p>Request a copy of your data we hold</p>
                      </div>

                      {/* Correction */}
                      <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                        <h3 className="text-lg font-semibold text-green-800 mb-2">Correction</h3>
                        <p>Update inaccurate or incomplete information</p>
                      </div>

                      {/* Deletion */}
                      <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                        <h3 className="text-lg font-semibold text-red-800 mb-2">Deletion</h3>
                        <p>Request erasure of your data under certain conditions</p>
                      </div>

                      {/* Withdraw Consent */}
                      <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Withdraw Consent</h3>
                        <p>Revoke permissions (e.g., marketing) at any time</p>
                      </div>

                      {/* Object/Restrict */}
                      <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Object/Restrict</h3>
                        <p>Object to processing or request temporary restriction</p>
                      </div>

                      {/* Portability */}
                      <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
                        <h3 className="text-lg font-semibold text-indigo-800 mb-2">Portability</h3>
                        <p>Receive your data in structured, machine-readable format</p>
                      </div>
                    </div>

                    {/* Opt-Out Notice */}
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-800 mb-2">Opt-Out of Sales:</h3>
                      <p>We do not sell your data (CCPA/GDPR compliance)</p>
                    </div>

                    {/* Exercise Rights */}
                    <div className="bg-white border border-blue-200 rounded-lg p-6 shadow-sm">
                      <h3 className="text-xl font-semibold text-blue-700 mb-4">To exercise these rights:</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">➔</span>
                          <span>Contact us at <a href="mailto:sales@vorstel.com" className="text-blue-600 hover:underline">contact@vorstel.com</a> or through our Privacy Request Portal</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">➔</span>
                          <span>We respond within 30 days and may verify your identity for security</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">➔</span>
                          <span>No fee required unless requests are manifestly unfounded or excessive</span>
                        </li>
                      </ul>
                    </div>

                    {/* Non-Discrimination */}
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="font-medium text-green-800">
                        You will not be discriminated against for exercising your privacy rights.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Data Security */}
                <section id="data-security" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">6.</span> Data Security
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    <p>
                      We implement industry-standard technical and organizational measures to protect your personal information:
                    </p>

                    {/* Security Measures */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-800 mb-3">Our Protections Include:</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">🔒</span>
                          <span>Encryption of data in transit (TLS/SSL) and at rest</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">🛡️</span>
                          <span>Access controls (role-based permissions, MFA)</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">🔍</span>
                          <span>Regular security audits and vulnerability testing</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">📜</span>
                          <span>Staff training on data handling and privacy practices</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">🤝</span>
                          <span>Vendor diligence requiring equivalent protections</span>
                        </li>
                      </ul>
                    </div>

                    {/* Limitations */}
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Important Limitations:</h3>
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium text-gray-800">No Absolute Security</h4>
                          <p>
                            While we use commercially reasonable safeguards, no digital system is 100% secure. We cannot guarantee absolute protection against unauthorized access, disclosure, or loss.
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">Breach Notification</h4>
                          <p>
                            We will notify you and regulators within legally mandated timeframes if a breach compromises your rights or freedoms.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* User Responsibilities */}
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Your Role:</h3>
                      <p className="mb-3">You are responsible for:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Keeping login credentials confidential</li>
                        <li>Using strong passwords and enabling available security features</li>
                        <li>Promptly reporting suspected security issues to <a href="mailto:sales@vorstel.com" className="text-blue-600 hover:underline">contact@vorstel.com</a></li>
                        <li>Logging out after sessions on shared devices</li>
                      </ul>
                    </div>

                    {/* Additional Note */}
                    <div className="bg-gray-50 p-4 rounded-lg text-sm">
                      <p>
                        <span className="font-medium">Note:</span> Our security measures evolve regularly to address new threats and technological advancements.
                      </p>
                    </div>
                  </div>
                </section>


                {/* Changes to This Policy */}
                <section id="policy-changes" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">7.</span> Changes to This Policy
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    <p>
                      We may update this Privacy Policy periodically to reflect changes in our practices or legal obligations.
                    </p>

                    {/* Notification Methods */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Notification of Changes:</h3>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>
                          <span className="font-medium">Revised Policy Posted:</span> We will post the updated policy on this page with a new "Last Updated" date
                        </li>
                        <li>
                          <span className="font-medium">Email Notice:</span> For significant changes affecting your rights, we may notify you via email (if we hold your contact details)
                        </li>
                      </ul>
                    </div>

                    {/* Acceptance & Review */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Continued Use</h3>
                        <p>Your continued use of the Site after changes constitutes acceptance of the updated policy.</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                        <h3 className="text-lg font-semibold text-green-800 mb-2">Regular Review</h3>
                        <p>We encourage you to review this page periodically for any updates.</p>
                      </div>
                    </div>

                    {/* Change Examples */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-800 mb-2">What Constitutes a Material Change?</h3>
                      <p className="text-sm">
                        Examples include: new data collection purposes, significant changes to your rights, or changes to international transfer mechanisms.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Contact Us */}
                <section id="contact-us" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">8.</span> Contact
                  </h2>
                  <div className="space-y-4 text-gray-600">
                    <p>
                      For any questions about these Terms, please contact us at:
                    </p>
                    <div className="bg-blue-50 p-4 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-blue-800 mb-2">Mailing Address:</h4>
                        <p>Richard-Wagner-Straße 4,</p>
                          <p>68165, Mannheim</p>
                          <p>Deutschland</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-blue-800 mb-2">Contact Information:</h4>
                         <p>Email:contact@vorstel.com</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Vorstel. All rights reserved.</p>
          {/* <div className="mt-2 flex justify-center space-x-6">
            <a href="#" className="text-gray-500 hover:text-gray-700">Privacy Policy</a>
            <a href="#" className="text-gray-500 hover:text-gray-700">Terms of Service</a>
            <a href="#" className="text-gray-500 hover:text-gray-700">Cookies Policy</a>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 